import {
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import type {
  AnswersImportPayload,
  CatalogueItem,
  Framing,
  LocalSession,
  Participant,
  Seat,
  Stance,
  StanceDefault,
  TakeLocalRoomOnlineOptions,
} from "../types";
import { CATEGORIES } from "../data/catalogue";
import type { OwnAnswersImport, SessionExport } from "../lib/export";
import {
  buildOwnAnswersCsvFile,
  MAX_OWN_ANSWERS_CSV_BYTES,
  parseOwnAnswersCsv,
  usefulRecords,
} from "../lib/export";
import { answerHasContent } from "../lib/session";
import { randomToken } from "../lib/crypto";
import { InlineAnswerSummary } from "./ResponseControls";
import { CopyIcon } from "./Icons";
import { FibonacciSpiral } from "./FibonacciSpiral";

export function Modal({
  title,
  children,
  onClose,
  className = "",
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  className?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const first =
      dialog.querySelector<HTMLElement>("[data-modal-autofocus]") ??
      dialog.querySelector<HTMLElement>("button, input, textarea, select");
    first?.focus();
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [onClose]);

  return (
    <dialog
      ref={ref}
      open
      className={`modal ${className}`}
      aria-modal="true"
      aria-labelledby="modal-title"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="modal-surface glass-panel">
        <header>
          <h2 id="modal-title">{title}</h2>
          <button
            className="icon-button"
            aria-label={`Close ${title}`}
            onClick={onClose}
          >
            ×
          </button>
        </header>
        {children}
      </div>
    </dialog>
  );
}

export function ProfileModal({
  participant,
  onSave,
  onClose,
}: {
  participant: Participant;
  onSave: (participant: Participant) => Promise<void>;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState(participant);
  const stances: Array<{ value: Stance; label: string }> = [
    { value: "dominant", label: "Dominant" },
    { value: "submissive", label: "Submissive" },
    { value: "switch", label: "Switch" },
  ];
  return (
    <Modal title="Your defaults" onClose={onClose} className="compact-modal">
      <form
        className="modal-form"
        onSubmit={(event) => {
          event.preventDefault();
          void onSave({ ...draft, name: draft.name.trim() }).then(onClose);
        }}
      >
        <label>
          <span>Name</span>
          <input
            value={draft.name}
            maxLength={40}
            onChange={(event) =>
              setDraft({ ...draft, name: event.target.value })
            }
          />
        </label>
        <fieldset>
          <legend>Starting stance</legend>
          <div className="segmented-control">
            {stances.map((stance) => (
              <button
                type="button"
                key={stance.value}
                aria-pressed={draft.stance === stance.value}
                className={draft.stance === stance.value ? "selected" : ""}
                onClick={() => setDraft({ ...draft, stance: stance.value })}
              >
                {stance.label}
              </button>
            ))}
          </div>
        </fieldset>
        <p className="form-hint">
          This changes only items with a stance-led starting side. Neutral
          items keep both perspectives open. Existing answers stay as you set
          them.
        </p>
        <button className="button primary">Save defaults</button>
      </form>
    </Modal>
  );
}

export function CustomEntryModal({
  seat,
  onAdd,
  onClose,
}: {
  seat: Seat;
  onAdd: (item: CatalogueItem) => Promise<void>;
  onClose: () => void;
}) {
  const [term, setTerm] = useState("");
  const [definition, setDefinition] = useState("");
  const [framing, setFraming] = useState<Framing>("shared");
  const [stanceDefault, setStanceDefault] = useState<StanceDefault>("both");
  const [first, setFirst] = useState("Give or do");
  const [second, setSecond] = useState("Receive");
  const [error, setError] = useState("");

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!term.trim()) {
      setError("Add the term you want to discuss.");
      return;
    }
    if (framing === "role-paired" && (!first.trim() || !second.trim())) {
      setError("Name both complementary perspectives.");
      return;
    }
    const perspectives =
      framing === "shared"
        ? ["Participate"]
        : framing === "directional"
          ? ["Give or do", "Receive"]
          : [first.trim(), second.trim()];
    const item: CatalogueItem = {
      id: `custom.${randomToken(9)}`,
      term: term.trim(),
      definition:
        definition.trim() || "A room-local term added for this conversation.",
      aliases: [],
      framing,
      stanceDefault: framing === "shared" ? "both" : stanceDefault,
      perspectives,
      categoryId: "custom",
      categoryName: "Custom terms",
      subgroupId: null,
      subgroupName: null,
      custom: true,
      createdBy: seat,
      locked: false,
      customVersion: 0,
    };
    await onAdd(item);
  };

  return (
    <Modal title="Add a term" onClose={onClose}>
      <form className="modal-form" onSubmit={(event) => void submit(event)}>
        <p className="form-hint">
          The term and reference description are shared immediately. Put
          answer-revealing detail in personal context.
        </p>
        <label>
          <span>Term</span>
          <input
            value={term}
            maxLength={100}
            onChange={(event) => setTerm(event.target.value)}
          />
        </label>
        <label>
          <span>Shared reference description <small>optional</small></span>
          <textarea
            value={definition}
            maxLength={500}
            onChange={(event) => setDefinition(event.target.value)}
          />
        </label>
        <fieldset>
          <legend>Response framing</legend>
          <div className="framing-choices">
            <button
              type="button"
              className={framing === "shared" ? "selected" : ""}
              aria-pressed={framing === "shared"}
              onClick={() => setFraming("shared")}
            >
              <span>Together</span>
              <small>One participation perspective</small>
            </button>
            <button
              type="button"
              className={framing === "directional" ? "selected" : ""}
              aria-pressed={framing === "directional"}
              onClick={() => setFraming("directional")}
            >
              <span>Giving + receiving</span>
              <small>General action labels</small>
            </button>
            <button
              type="button"
              className={framing === "role-paired" ? "selected" : ""}
              aria-pressed={framing === "role-paired"}
              onClick={() => setFraming("role-paired")}
            >
              <span>Named roles</span>
              <small>Two complementary labels</small>
            </button>
          </div>
        </fieldset>
        {framing !== "shared" ? (
          <fieldset>
            <legend>Starting perspectives</legend>
            <div className="framing-choices">
              <button
                type="button"
                className={stanceDefault === "both" ? "selected" : ""}
                aria-pressed={stanceDefault === "both"}
                onClick={() => setStanceDefault("both")}
              >
                <span>Both start open</span>
                <small>Stance does not choose a side</small>
              </button>
              <button
                type="button"
                className={stanceDefault === "dominant-first" ? "selected" : ""}
                aria-pressed={stanceDefault === "dominant-first"}
                onClick={() => setStanceDefault("dominant-first")}
              >
                <span>
                  {framing === "directional"
                    ? "Dominant gives"
                    : "Dominant starts first"}
                </span>
                <small>
                  {framing === "directional"
                    ? "Submissive receives"
                    : "Submissive starts second"}
                </small>
              </button>
              <button
                type="button"
                className={stanceDefault === "dominant-second" ? "selected" : ""}
                aria-pressed={stanceDefault === "dominant-second"}
                onClick={() => setStanceDefault("dominant-second")}
              >
                <span>
                  {framing === "directional"
                    ? "Dominant receives"
                    : "Dominant starts second"}
                </span>
                <small>
                  {framing === "directional"
                    ? "Submissive gives"
                    : "Submissive starts first"}
                </small>
              </button>
            </div>
          </fieldset>
        ) : null}
        {framing === "role-paired" ? (
          <div className="paired-inputs">
            <label>
              <span>First perspective</span>
              <input
                value={first}
                maxLength={80}
                onChange={(event) => setFirst(event.target.value)}
              />
            </label>
            <label>
              <span>Complementary perspective</span>
              <input
                value={second}
                maxLength={80}
                onChange={(event) => setSecond(event.target.value)}
              />
            </label>
          </div>
        ) : null}
        {error ? <p className="inline-error" role="alert">{error}</p> : null}
        <button className="button primary">Add to this room</button>
      </form>
    </Modal>
  );
}

export function InviteModal({
  partnerInvite,
  onClose,
}: {
  partnerInvite: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(partnerInvite);
    setCopied(true);
  };
  return (
    <Modal title="Invite your partner" onClose={onClose} className="compact-modal">
      <div className="invite-panel">
        <p>
          This complete link contains their private seat capability and the
          room’s decryption key. Share it directly.
        </p>
        <label>
          <span>Partner link</span>
          <textarea readOnly value={partnerInvite} />
        </label>
        <button
          className="button primary icon-label-button"
          onClick={() => void copy()}
        >
          <CopyIcon />
          <span>{copied ? "Copied" : "Copy partner link"}</span>
        </button>
      </div>
    </Modal>
  );
}

export function TakeOnlineModal({
  session,
  onCreate,
  onClose,
}: {
  session: LocalSession;
  onCreate: (options: TakeLocalRoomOnlineOptions) => Promise<void>;
  onClose: () => void;
}) {
  const [selection, setSelection] = useState<
    TakeLocalRoomOnlineOptions["selection"]
  >("both");
  const [newPartner, setNewPartner] = useState<Participant>({
    name: "New partner",
    stance: "switch",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const savedCount = (seat: Seat) =>
    Object.values(session.answers[seat]).filter(
      (answer) => answer.savedAt !== null,
    ).length;
  const choices: Array<{
    value: TakeLocalRoomOnlineOptions["selection"];
    label: string;
    detail: string;
  }> = [
    {
      value: "both",
      label: "Both people",
      detail: "Continue together with both sides, shared notes, and custom terms.",
    },
    {
      value: "A",
      label: `${session.participants.A.name} only · ${savedCount("A")} saved`,
      detail: "Start a fresh comparison without the other side or shared notes.",
    },
    {
      value: "B",
      label: `${session.participants.B.name} only · ${savedCount("B")} saved`,
      detail: "Use this person’s side as the online room creator’s answers.",
    },
  ];

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (selection !== "both" && !newPartner.name.trim()) {
      setError("Name the new partner before creating the room.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await onCreate({ selection, newPartner });
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "The online room could not be created.",
      );
      setBusy(false);
    }
  };

  return (
    <Modal title="Take this session online" onClose={onClose}>
      <form className="modal-form take-online-panel" onSubmit={submit}>
        <p>
          Choose what the new 30-day room starts with. This local session
          stays on this device.
        </p>
        <fieldset className="copy-scope-choices">
          <legend>Copy into the new room</legend>
          {choices.map((choice) => (
            <button
              type="button"
              key={choice.value}
              className={selection === choice.value ? "selected" : ""}
              aria-pressed={selection === choice.value}
              onClick={() => setSelection(choice.value)}
            >
              <span>{choice.label}</span>
              <small>{choice.detail}</small>
            </button>
          ))}
        </fieldset>
        {selection !== "both" ? (
          <div className="new-partner-fields">
            <label>
              <span>New partner name</span>
              <input
                value={newPartner.name}
                maxLength={40}
                autoComplete="off"
                onChange={(event) =>
                  setNewPartner({ ...newPartner, name: event.target.value })
                }
              />
            </label>
            <fieldset>
              <legend>New partner’s starting stance</legend>
              <div className="segmented-control">
                {(["dominant", "submissive", "switch"] as const).map(
                  (stance) => (
                    <button
                      type="button"
                      key={stance}
                      className={newPartner.stance === stance ? "selected" : ""}
                      aria-pressed={newPartner.stance === stance}
                      onClick={() => setNewPartner({ ...newPartner, stance })}
                    >
                      {stance[0].toUpperCase() + stance.slice(1)}
                    </button>
                  ),
                )}
              </div>
            </fieldset>
          </div>
        ) : null}
        {error ? <p className="inline-error" role="alert">{error}</p> : null}
        <button className="button primary" disabled={busy}>
          {busy ? "Creating online copy…" : "Create online room"}
        </button>
      </form>
    </Modal>
  );
}

export function ExportModal({
  data,
  onImport,
  onClose,
}: {
  data: SessionExport;
  onImport: (payload: AnswersImportPayload) => Promise<void>;
  onClose: () => void;
}) {
  const inputId = useId();
  const [preview, setPreview] = useState<OwnAnswersImport | null>(null);
  const [filename, setFilename] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [complete, setComplete] = useState("");
  const [csvDownload, setCsvDownload] = useState<{
    href: string;
    filename: string;
  } | null>(null);
  const useful = usefulRecords(data);
  const ownAnswerCount = data.records.filter((record) => {
    const answer = record.answers[data.viewerSeat];
    return answer !== null && answer !== "redacted" && answerHasContent(answer);
  }).length;
  useEffect(() => {
    setCsvDownload(null);
    if (ownAnswerCount === 0) return;
    const file = buildOwnAnswersCsvFile(data);
    const href = URL.createObjectURL(
      new Blob([file.contents], { type: file.type }),
    );
    setCsvDownload({ href, filename: file.filename });
    return () => URL.revokeObjectURL(href);
  }, [data, ownAnswerCount]);
  const printReport = () => {
    const previousTitle = document.title;
    document.title = `${data.participants.A.name} + ${data.participants.B.name} - Feel Scene`;
    window.print();
    document.title = previousTitle;
  };
  const chooseCsv = async (file: File | undefined) => {
    setPreview(null);
    setComplete("");
    setError("");
    setFilename(file?.name ?? "");
    if (!file) return;
    if (file.size > MAX_OWN_ANSWERS_CSV_BYTES) {
      setError("This CSV is larger than 5 MB. Choose a Feel Scene answers CSV.");
      return;
    }
    setBusy(true);
    try {
      setPreview(parseOwnAnswersCsv(await file.text(), data));
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "This CSV could not be read. Choose a Feel Scene answers CSV.",
      );
    } finally {
      setBusy(false);
    }
  };
  const applyImport = async () => {
    if (!preview) return;
    setBusy(true);
    setError("");
    try {
      await onImport(preview);
      setComplete(
        `${preview.answers.length} ${preview.answers.length === 1 ? "answer" : "answers"} imported to your side.`,
      );
      setPreview(null);
      setFilename("");
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Your answers could not be imported. Try again.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title="Export & import" onClose={onClose}>
      <div className="export-panel">
        <p>
          Keep your answers for another session. Your CSV never includes your
          partner’s answers or shared notes.
        </p>
        <dl className="export-stats">
          <div><dt>My answers</dt><dd>{ownAnswerCount}</dd></div>
          <div><dt>Discussed</dt><dd>{useful.length}</dd></div>
          <div><dt>Catalogue</dt><dd>v{data.catalogueVersion}</dd></div>
        </dl>
        <section className="export-section">
          <h3>Export</h3>
          <p className="form-hint">
            Print the current shared view, or download only your reusable answers.
          </p>
          <div className="export-actions">
            <button className="button secondary" onClick={printReport}>
              Print shared report
            </button>
            <a
              className="button primary"
              data-modal-autofocus={ownAnswerCount > 0 ? true : undefined}
              href={csvDownload?.href}
              download={csvDownload?.filename}
              aria-disabled={!csvDownload}
              tabIndex={csvDownload ? undefined : -1}
              onClick={(event) => {
                if (!csvDownload) event.preventDefault();
              }}
            >
              Download my answers (.csv)
            </a>
          </div>
        </section>
        <section className="export-section import-section">
          <h3>Import</h3>
          <p className="form-hint">
            Fill your side from a Feel Scene answers CSV, then tune it for this
            partner. Existing answers for matching items will be replaced.
          </p>
          <div className="import-picker">
            <label className="button secondary" htmlFor={inputId}>
              Choose answers CSV
            </label>
            <input
              id={inputId}
              className="sr-only"
              type="file"
              accept=".csv,text/csv"
              disabled={busy}
              onChange={(event) => void chooseCsv(event.target.files?.[0])}
            />
            <span>{filename || "No file chosen"}</span>
          </div>
          {busy && !preview ? <p role="status">Reading your answers…</p> : null}
          {preview ? (
            <div className="import-preview" role="status">
              <p>
                Ready to import {preview.answers.length}{" "}
                {preview.answers.length === 1 ? "answer" : "answers"}.
                {preview.replacementCount > 0
                  ? ` ${preview.replacementCount} existing ${preview.replacementCount === 1 ? "answer" : "answers"} will be replaced.`
                  : " Your existing answers will stay unchanged."}
              </p>
              {preview.customEntries.length > 0 ? (
                <p>
                  Includes {preview.customEntries.length}{" "}
                  {preview.customEntries.length === 1 ? "custom term" : "custom terms"}.
                </p>
              ) : null}
              {preview.skippedItemIds.length > 0 ? (
                <p>
                  {preview.skippedItemIds.length}{" "}
                  {preview.skippedItemIds.length === 1 ? "older item is" : "older items are"}{" "}
                  no longer in this catalogue and will be skipped.
                </p>
              ) : null}
              <button
                className="button primary"
                disabled={busy}
                onClick={() => void applyImport()}
              >
                {busy
                  ? "Importing…"
                  : `Import ${preview.answers.length} ${preview.answers.length === 1 ? "answer" : "answers"}`}
              </button>
            </div>
          ) : null}
          {error ? <p className="inline-error" role="alert">{error}</p> : null}
          {complete ? <p className="save-status saved" role="status">{complete}</p> : null}
        </section>
      </div>
    </Modal>
  );
}

export function ConfirmModal({
  title,
  message,
  confirmLabel,
  onConfirm,
  onClose,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Modal title={title} onClose={onClose} className="compact-modal">
      <div className="confirm-panel">
        <p>{message}</p>
        <div>
          <button className="button secondary" onClick={onClose}>Keep session</button>
          <button className="button danger-button" onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export function PrintableReport({ data }: { data: SessionExport }) {
  const records = usefulRecords(data);
  const categories = records.reduce<
    Array<{
      id: string;
      name: string;
      number: number | null;
      records: SessionExport["records"];
    }>
  >((groups, record) => {
    const previous = groups.at(-1);
    if (previous?.id === record.item.categoryId) {
      previous.records.push(record);
      return groups;
    }
    groups.push({
      id: record.item.categoryId,
      name: record.item.categoryName,
      number:
        CATEGORIES.find((category) => category.id === record.item.categoryId)
          ?.number ?? null,
      records: [record],
    });
    return groups;
  }, []);
  const preparedAt = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(data.exportedAt));
  const itemCountLabel =
    records.length === 1 ? "item in this report" : "items in this report";

  return (
    <section className="print-report">
      <section className="print-cover">
        <FibonacciSpiral className="print-cover-spiral" />
        <header className="print-cover-brand">
          <img src="/logo-mark.svg" alt="" />
          <h1>Feel Scene</h1>
        </header>
        <div className="print-cover-title">
          <h2>
            {data.participants.A.name}
            <span aria-hidden="true">+</span>
            {data.participants.B.name}
          </h2>
          <p>Our shared scene map</p>
        </div>
        <div className="print-cover-summary">
          <p className="print-conversation-count">
            <strong>{records.length}</strong>
            <span>{itemCountLabel}</span>
          </p>
          <dl>
            <div>
              <dt>Prepared</dt>
              <dd>
                <time dateTime={data.exportedAt}>{preparedAt}</time>
              </dd>
            </div>
            <div>
              <dt>Catalogue</dt>
              <dd>{data.catalogueVersion}</dd>
            </div>
          </dl>
        </div>
      </section>

      <div className="print-report-body">
        {categories.map((category) => (
          <table className="print-category" key={category.id} role="presentation">
            <thead>
              <tr>
                <td>
                  <header className="print-category-header">
                    <div className="print-category-title">
                      <span className="print-category-number">
                        {category.number === null
                          ? "—"
                          : String(category.number).padStart(2, "0")}
                      </span>
                      <h2>{category.name}</h2>
                    </div>
                    {(["A", "B"] as const).map((seat) => (
                      <div className="print-category-participant-name" key={seat}>
                        {data.participants[seat].name}
                      </div>
                    ))}
                  </header>
                </td>
              </tr>
            </thead>
            <tbody>
              {category.records.map((record) => (
                <tr className="print-entry-row" key={record.item.id}>
                  <td>
                    <article
                      className={`print-entry ${record.note?.important ? "is-bookmarked" : ""}`}
                    >
                      <header className="print-entry-header">
                        <div className="print-entry-title-line">
                          <h3>{record.item.term}</h3>
                          {record.item.custom ? (
                            <span className="print-custom-entry">Custom</span>
                          ) : null}
                          {record.note?.important ? (
                            <span className="print-bookmarked">Bookmarked</span>
                          ) : null}
                        </div>
                        <p>{record.item.definition}</p>
                      </header>

                      <div className="print-answers">
                        {(["A", "B"] as const).map((seat) => {
                          const answer = record.answers[seat];
                          const participant = data.participants[seat].name;
                          return (
                            <section className="print-participant" key={seat}>
                              {answer === "redacted" ? (
                                <>
                                  <h4 className="sr-only">{participant}</h4>
                                  <p className="print-answer-state">
                                    <strong>Saved</strong>
                                    <span>Hidden until both save</span>
                                  </p>
                                </>
                              ) : answer ? (
                                <>
                                  <InlineAnswerSummary
                                    answer={answer}
                                    participant={participant}
                                    hideParticipant
                                  />
                                  {answer.context ? (
                                    <div className="print-personal-copy">
                                      <section
                                        className={answer.important ? "is-important" : ""}
                                      >
                                        <h4>
                                          {answer.important
                                            ? "Important context"
                                            : "Context"}
                                        </h4>
                                        <p>{answer.context}</p>
                                      </section>
                                    </div>
                                  ) : null}
                                </>
                              ) : (
                                <>
                                  <h4 className="sr-only">{participant}</h4>
                                  <p className="print-answer-state">Unanswered</p>
                                </>
                              )}
                            </section>
                          );
                        })}
                      </div>

                      {record.note?.text || record.note?.important ? (
                        <aside className="print-shared-note">
                          <span aria-hidden="true" />
                          <div>
                            <h4>{record.note.text ? "Shared note" : "Bookmarked"}</h4>
                            {record.note.text ? <p>{record.note.text}</p> : null}
                          </div>
                        </aside>
                      ) : null}
                    </article>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}
      </div>
      <footer className="print-running-footer" aria-hidden="true">
        <span>Feel Scene</span>
        <span>{data.participants.A.name} + {data.participants.B.name}</span>
      </footer>
    </section>
  );
}
